import { Label, makeStyles, shorthands } from "@fluentui/react-components";
import { Info20Regular } from "@fluentui/react-icons";
import i18next from "i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    AdminFeedback,
    AppType,
    ChatAppResponse,
    DataPoints,
    feedbackApi,
    getAppTypeBasedOnLastAnswerIsRelatedToAriel,
    loadChatApi,
    loadFilteredSessionsApi,
    loadSessionSummaryApi,
    loadUsersApi,
    QuestionDetails,
    regenerateSessionSummaryApi,
    SaveResponse,
    SessionSummaryResponse,
    SessionSummaryLocalized,
    SessionSummaryTopic,
    SessionInfo,
    YearData,
    Histories,
    loadAnswerRowsFromTree,
    buildTree,
    TreeNode
} from "../../api";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { parseAnswerToHtml } from "../../components/Answer/AnswerParser";
import { AnswerRegenarate } from "../../components/Answer/AnswerRegenarate";
import { UnifiedAnswer } from "../../components/Answer/UnifiedAnswer";
import { Filters } from "../../components/Filters";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { UserChatMessage, UserChatMessageRef } from "../../components/UserChatMessage";
import styles from "./Chat.module.css";
import "./Filters.css";
import { appInfo, isAdmin, isWapUser, userInfo } from "../../authConfig";
import { useNavigate } from "react-router-dom";
import { FeedBackPageComponentReducerAction } from "../../Store/FeedBackPageComponentReducer";
import { useDispatch, useSelector } from "react-redux";
import { selectFeedbackCurrentSessionId } from "../../Store";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import SessionNotFound from "../../components/Sidebar/SessionNotFound";
import { getValidAppType } from "./utils";
import { extractAttachmentNamesFromDataPoints, isPartialAttachmentNameMatch, normalizeAttachmentName } from "../../utils/AttachmentNameUtils";
import { useThoughtsLoader } from "./hooks/useThoughtsLoader";

const useStyles = makeStyles({
    root: {
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        ...shorthands.padding("10px", "20px"),
        rowGap: "10px"
    },
    tabStyle: {
        color: "white",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        lineHeight: 2,
        fontSize: "15px",
        cursor: "pointer",
        ":active": {
            fontWeight: "bold"
        },
        ":selected": {
            fontWeight: "bold"
        },
        ":hover": {
            fontWeight: "bold"
        },
        ":target": {
            fontWeight: "bold"
        }
    }
});

interface Users {
    key: string;
    text: string;
    corp_id: string;
    corp_name: string;
}

interface Corps {
    key: string;
    text: string;
}

interface Sessions {
    session_id: string;
    title: string;
    user_id: string;
    corp_id: string;
    conversation_date: string;
}

const masterList: {
    users: Users[];
    corps: Corps[];
    monthlySessions: any;
    todaySessionsSource: Sessions[];
    last7SessionsSource: Sessions[];
    last30SessionsSource: Sessions[];
} = {
    users: [],
    corps: [],
    monthlySessions: [],
    todaySessionsSource: [],
    last7SessionsSource: [],
    last30SessionsSource: []
};
const filteredData: { users: Users[]; corps: Corps[] } = {
    users: [],
    corps: []
};

interface SessionSummaryPanelProps {
    isVisible: boolean;
    sessionId: string;
    conversationCount: number;
    onTopicClick: (questionId: string) => void;
    corpId?: string;
    userName?: string;
    products?: string[];
    lastConversationDate?: string;
}

const _normTopics = (raw: any[]): SessionSummaryTopic[] =>
    raw
        .filter((t: any) => t && typeof t === "object" && t.question_id)
        .map((t: any) => ({
            question_id: String(t.question_id ?? ""),
            topic_label: String(t.topic_label ?? "")
        }));

const normalizeSummaryResponse = (rawSummary: any): SessionSummaryResponse => {
    const topics = _normTopics(Array.isArray(rawSummary?.topics) ? rawSummary.topics : []);

    const rawSummaries = rawSummary?.summaries ?? {};
    const summaries: Record<string, SessionSummaryLocalized> = {};
    if (rawSummaries && typeof rawSummaries === "object") {
        for (const [lang, ld] of Object.entries(rawSummaries) as [string, any][]) {
            if (ld && typeof ld === "object") {
                summaries[lang] = {
                    overall_summary: String(ld.overall_summary ?? ""),
                    topics: _normTopics(Array.isArray(ld.topics) ? ld.topics : [])
                };
            }
        }
    }

    return {
        session_id: String(rawSummary?.session_id ?? ""),
        overall_summary: String(rawSummary?.overall_summary ?? ""),
        summary_generated_at: String(rawSummary?.summary_generated_at ?? ""),
        summary_error: String(rawSummary?.summary_error ?? ""),
        conversation_count: Number(rawSummary?.conversation_count ?? 0),
        topics,
        summary_language: String(rawSummary?.summary_language ?? ""),
        summaries
    };
};

const inFlightSummaryLoads = new Map<string, Promise<SessionSummaryResponse>>();

const loadSessionSummaryOncePerSession = async (sessionId: string): Promise<SessionSummaryResponse> => {
    const existingPromise = inFlightSummaryLoads.get(sessionId);
    if (existingPromise) {
        return existingPromise;
    }

    const requestPromise = (async () => {
        const response = await loadSessionSummaryApi(sessionId);
        const result = await response.json();
        if (!response.ok || result?.error) {
            throw new Error(result?.error || "Failed to load session summary");
        }
        return normalizeSummaryResponse(result);
    })();

    inFlightSummaryLoads.set(sessionId, requestPromise);
    try {
        return await requestPromise;
    } finally {
        inFlightSummaryLoads.delete(sessionId);
    }
};

const SessionSummaryPanel = React.memo(
    ({ isVisible, sessionId, conversationCount, onTopicClick, corpId, userName, products, lastConversationDate }: SessionSummaryPanelProps) => {
        const [sessionSummary, setSessionSummary] = useState<SessionSummaryResponse | null>(null);
        const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
        const [isSummaryRegenerating, setIsSummaryRegenerating] = useState<boolean>(false);
        const requestCounterRef = useRef(0);
        const [floatPos, setFloatPos] = useState({ x: -1, y: -1 });
        const [isDragging, setIsDragging] = useState(false);
        const [showTopics, setShowTopics] = useState(false);
        const dragOffsetRef = useRef({ x: 0, y: 0 });
        const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const btnRef = useRef<HTMLButtonElement | null>(null);
        const wrapperRef = useRef<HTMLDivElement | null>(null);
        const panelRef = useRef<HTMLDivElement | null>(null);
        const didDragRef = useRef(false);

        useEffect(() => {
            const vw = () => window.visualViewport?.width ?? window.innerWidth;
            const vh = () => window.visualViewport?.height ?? window.innerHeight;

            const updatePos = () => {
                const btnW = btnRef.current?.offsetWidth ?? 36;
                const btnH = btnRef.current?.offsetHeight ?? 36;
                if (panelRef.current) {
                    const rect = panelRef.current.getBoundingClientRect();
                    setFloatPos({
                        x: Math.max(0, Math.min(rect.right - 46, Math.max(0, vw() - btnW))),
                        y: Math.max(0, Math.min(rect.bottom - 46, Math.max(0, vh() - btnH)))
                    });
                } else {
                    setFloatPos({ x: Math.max(0, vw() - 46), y: Math.max(0, vh() - 46) });
                }
            };

            updatePos();

            window.addEventListener("resize", updatePos);
            window.visualViewport?.addEventListener("resize", updatePos);
            return () => {
                window.removeEventListener("resize", updatePos);
                window.visualViewport?.removeEventListener("resize", updatePos);
            };
        }, []);

        useEffect(() => {
            if (!isDragging) return;
            const onMouseMove = (e: MouseEvent) => {
                didDragRef.current = true;
                const btnW = btnRef.current?.offsetWidth ?? 36;
                const btnH = btnRef.current?.offsetHeight ?? 36;
                const x = Math.min(Math.max(0, e.clientX - dragOffsetRef.current.x), window.innerWidth - btnW);
                const y = Math.min(Math.max(0, e.clientY - dragOffsetRef.current.y), window.innerHeight - btnH);
                setFloatPos({ x, y });
            };
            const onMouseUp = () => setIsDragging(false);
            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
            return () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            };
        }, [isDragging]);

        const handleBtnMouseDown = (e: React.MouseEvent) => {
            const target = e.target as Node | null;
            if (btnRef?.current && target && !btnRef?.current?.contains(target)) {
                return;
            }
            didDragRef.current = false;
            dragOffsetRef.current = { x: e.clientX - floatPos.x, y: e.clientY - floatPos.y };
            setIsDragging(true);
            setShowTopics(false);
            e.preventDefault();
        };

        const handleBtnClick = () => {
            if (!didDragRef.current) {
                setShowTopics(prev => !prev);
            }
        };

        const handleBtnKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowTopics(prev => !prev);
            }
        };

        const handleFloatMouseEnter = () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            if (!isDragging) setShowTopics(true);
        };

        const handleFloatMouseLeave = () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            hideTimerRef.current = setTimeout(() => {
                hideTimerRef.current = null;
                setShowTopics(false);
            }, 150);
        };

        useEffect(() => {
            return () => {
                if (hideTimerRef.current) {
                    clearTimeout(hideTimerRef.current);
                    hideTimerRef.current = null;
                }
            };
        }, []);

        useEffect(() => {
            const requestId = ++requestCounterRef.current;
            if (!isVisible) {
                setSessionSummary(null);
                setIsSummaryLoading(false);
                setIsSummaryRegenerating(false);
                return;
            }

            if (!sessionId) {
                setSessionSummary(null);
                setIsSummaryLoading(false);
                setIsSummaryRegenerating(false);
                return;
            }

            setSessionSummary(null);
            setIsSummaryRegenerating(false);
            setIsSummaryLoading(true);

            const loadSummary = async () => {
                try {
                    const normalizedSummary = await loadSessionSummaryOncePerSession(sessionId);
                    if (requestCounterRef.current !== requestId) {
                        return;
                    }
                    setSessionSummary(normalizedSummary);
                } catch (error) {
                    if (requestCounterRef.current !== requestId) {
                        return;
                    }
                    console.error("Error loading session summary:", error);
                    setSessionSummary(null);
                } finally {
                    if (requestCounterRef.current === requestId) {
                        setIsSummaryLoading(false);
                    }
                }
            };

            void loadSummary();
        }, [isVisible, sessionId]);

        const regenerateSessionSummary = async () => {
            if (!sessionId) {
                return;
            }
            const requestId = ++requestCounterRef.current;
            try {
                setIsSummaryRegenerating(true);
                setIsSummaryLoading(true);
                const response = await regenerateSessionSummaryApi(sessionId, sessionStorage.getItem("selectedLocale") ?? undefined);
                const result = await response.json();
                if (!response.ok || result?.error) {
                    throw new Error(result?.error || "Failed to regenerate summary");
                }
                if (requestCounterRef.current !== requestId) {
                    return;
                }
                setSessionSummary(normalizeSummaryResponse(result));
            } catch (error) {
                if (requestCounterRef.current !== requestId) {
                    return;
                }
                console.error("Error regenerating session summary:", error);
            } finally {
                if (requestCounterRef.current === requestId) {
                    setIsSummaryRegenerating(false);
                    setIsSummaryLoading(false);
                }
            }
        };

        const getSummaryMetaText = () => {
            if (!sessionSummary?.summary_generated_at) {
                return "";
            }
            const rawTime = sessionSummary.summary_generated_at;
            try {
                const date = new Date(rawTime);
                if (!isNaN(date.getTime())) {
                    return `${i18next.t("feedbackSummary.labels.lastGenerated")}: ${date.toLocaleString(i18next.language)}`;
                }
            } catch {
                // fall through to raw value
            }
            return `${i18next.t("feedbackSummary.labels.lastGenerated")}: ${rawTime}`;
        };

        if (!isVisible) {
            return (
                <div ref={panelRef} className={styles.feedbackSummaryPanel}>
                    <div className={styles.feedbackSummaryHeader}>
                        <div className={styles.feedbackSummaryTitle}>{i18next.t("feedbackSummary.title")}</div>
                    </div>
                    <div className={styles.feedbackSummaryEmptyState}>
                        <div className={styles.feedbackSummaryEmptyIcon}>&#9741;</div>
                        <div className={styles.feedbackSummaryEmptyText}>{i18next.t("feedbackSummary.messages.selectSession")}</div>
                    </div>
                </div>
            );
        }

        const summaryMetaText = getSummaryMetaText();
        const productList = (products ?? []).filter(Boolean);

        // Pick the locale-specific summary variant for display.
        // Locale codes ("ja", "en") are mapped to language names that match the LLM generation keys.
        const LOCALE_TO_LANGUAGE: Record<string, string> = { ja: "Japanese", en: "English" };
        const displayLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
        const displayLanguage = LOCALE_TO_LANGUAGE[displayLocale] ?? "Japanese";
        const localizedEntry: SessionSummaryLocalized | null = sessionSummary?.summaries?.[displayLanguage] ?? null;
        const displaySummary = localizedEntry?.overall_summary ?? sessionSummary?.overall_summary ?? "";
        const displayTopics = localizedEntry?.topics ?? sessionSummary?.topics ?? [];

        const isOutdated = Boolean(
            sessionSummary &&
            lastConversationDate &&
            sessionSummary.summary_generated_at &&
            new Date(lastConversationDate) > new Date(sessionSummary.summary_generated_at)
        );
        const showRetryButton = Boolean(sessionSummary?.summary_error) || isOutdated;
        const popupAbove = floatPos.y > window.innerHeight / 2;
        const popupAlignRight = floatPos.x + (btnRef.current?.offsetWidth ?? 36) + 340 > window.innerWidth;
        const popupPositionStyle: React.CSSProperties = {
            ...(popupAbove ? { bottom: "100%", top: "auto" } : { top: "100%", bottom: "auto" }),
            ...(popupAlignRight ? { right: 0, left: "auto" } : { left: 0, right: "auto" })
        };

        return (
            <div ref={panelRef} className={styles.feedbackSummaryPanel}>
                {/* Row 1: title | [lastGenerated · convCount · corp · user] | conditional retry button */}
                <div className={styles.feedbackSummaryHeader}>
                    <div className={styles.feedbackSummaryTitle}>{i18next.t("feedbackSummary.title")}</div>
                    <div className={styles.feedbackSummaryHeaderMeta}>
                        {Boolean(summaryMetaText) && <span className={styles.feedbackSummaryMetaInHeader}>{summaryMetaText}</span>}
                        {(sessionSummary?.conversation_count ?? conversationCount) > 0 && (
                            <span className={styles.feedbackSummaryInfoChip}>
                                {i18next.t("feedbackSummary.labels.conversations")}: <strong>{sessionSummary?.conversation_count ?? conversationCount}</strong>
                            </span>
                        )}
                        {Boolean(corpId) && (
                            <span className={styles.feedbackSummaryInfoChip}>
                                {i18next.t("feedbackSummary.labels.corp")}: <strong>{corpId}</strong>
                            </span>
                        )}
                        {Boolean(userName) && (
                            <span className={styles.feedbackSummaryInfoChip}>
                                {i18next.t("feedbackSummary.labels.user")}: <strong>{userName}</strong>
                            </span>
                        )}
                    </div>
                    {showRetryButton && (
                        <button
                            type="button"
                            className={styles.feedbackSummaryRegenerateButton}
                            onClick={() => {
                                void regenerateSessionSummary();
                            }}
                            disabled={!sessionId || isSummaryLoading || isSummaryRegenerating}
                        >
                            {i18next.t("feedbackSummary.actions.retry")}
                        </button>
                    )}
                </div>

                <div className={styles.feedbackSummaryLeftPane}>
                    {productList.length > 0 && (
                        <div className={styles.feedbackSummaryProductsRow}>
                            <span className={styles.feedbackSummaryProductsLabel}>{i18next.t("feedbackSummary.labels.products")}:</span>
                            <span className={styles.feedbackSummaryProductsText}>{productList.join(", ")}</span>
                        </div>
                    )}
                    <div className={styles.feedbackSummaryBody}>
                        {isSummaryLoading ? (
                            <div className={styles.feedbackSummarySkeleton}>
                                <div className={`${styles.feedbackSummarySkeletonLine} ${styles.feedbackSummarySkeletonLineLong}`}></div>
                                <div className={`${styles.feedbackSummarySkeletonLine} ${styles.feedbackSummarySkeletonLineMid}`}></div>
                                <div className={`${styles.feedbackSummarySkeletonLine} ${styles.feedbackSummarySkeletonLineShort}`}></div>
                            </div>
                        ) : !sessionSummary ? (
                            <div className={styles.feedbackSummaryText}>{i18next.t("feedbackSummary.messages.empty")}</div>
                        ) : (
                            <div className={styles.feedbackSummaryText}>{displaySummary || i18next.t("feedbackSummary.messages.empty")}</div>
                        )}
                    </div>
                </div>

                {displayTopics.length > 0 && floatPos.x >= 0 && (
                    <div
                        ref={wrapperRef}
                        className={styles.topicsFloatingWrapper}
                        style={{ left: floatPos.x, top: floatPos.y, cursor: isDragging ? "grabbing" : "grab" }}
                        onMouseDown={handleBtnMouseDown}
                        onMouseEnter={handleFloatMouseEnter}
                        onMouseLeave={handleFloatMouseLeave}
                    >
                        <button
                            type="button"
                            ref={btnRef}
                            aria-label={i18next.t("feedbackSummary.labels.topicsDiscussed")}
                            aria-haspopup="true"
                            aria-expanded={showTopics && !isDragging}
                            className={styles.topicsFloatingBtn}
                            onClick={handleBtnClick}
                            onKeyDown={handleBtnKeyDown}
                            onFocus={handleFloatMouseEnter}
                            onBlur={e => {
                                const next = e.relatedTarget as Node | null;
                                if (next && wrapperRef.current?.contains(next)) return;
                                handleFloatMouseLeave();
                            }}
                        >
                            <Info20Regular aria-hidden="true" />
                        </button>
                        {showTopics && !isDragging && (
                            <div className={styles.topicsFloatingPopup} style={popupPositionStyle}>
                                <div className={styles.topicsFloatingPopupTitle}>{i18next.t("feedbackSummary.labels.topicsDiscussed")}</div>
                                <ol className={styles.topicsFloatingPopupList}>
                                    {displayTopics.map((topic, idx) => (
                                        <li key={idx} className={styles.topicsFloatingPopupItem}>
                                            <button
                                                type="button"
                                                className={styles.feedbackSummaryTopicLink}
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={() => {
                                                    onTopicClick(topic.question_id);
                                                    setShowTopics(false);
                                                }}
                                            >
                                                {topic.topic_label}
                                            </button>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

const Feedback = () => {
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const filtersRef = useRef<any>(null);
    const userChatMessageRefs = useRef<{ [key: number]: UserChatMessageRef | null }>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sessionNotFound, setSessionNotFound] = useState<boolean>(false);

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
    const [thoughtsLoadingIndex, setThoughtsLoadingIndex] = useState<number | null>(null);
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: QuestionDetails, response: { FAQ: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }][]>([]);

    const findAttachmentOwnerIndex = (
        filename: string,
        preferredIndex?: number,
        fallbackNames?: Set<string>
    ): { index: number; matchedName: string } | null => {
        const target = normalizeAttachmentName(filename);
        if (!target) return null;

        const findByName = (name: string, startIndex?: number) => {
            const matchesAt = (i: number) => {
                const details = answers[i]?.[0];
                const files = details?.filesAttached ?? [];
                if (!Array.isArray(files)) return null;
                for (const att of files) {
                    const attName = normalizeAttachmentName(att.file?.name);
                    if (attName === name || isPartialAttachmentNameMatch(attName, name)) {
                        return attName;
                    }
                }
                return null;
            };

            if (typeof startIndex === "number" && startIndex >= 0 && startIndex < answers.length) {
                const matchAtStart = matchesAt(startIndex);
                if (matchAtStart) return { index: startIndex, matchedName: matchAtStart };
                for (let i = startIndex - 1; i >= 0; i--) {
                    const matchAt = matchesAt(i);
                    if (matchAt) return { index: i, matchedName: matchAt };
                }
                for (let i = startIndex + 1; i < answers.length; i++) {
                    const matchAt = matchesAt(i);
                    if (matchAt) return { index: i, matchedName: matchAt };
                }
                return null;
            }

            for (let i = answers.length - 1; i >= 0; i--) {
                const matchAt = matchesAt(i);
                if (matchAt) return { index: i, matchedName: matchAt };
            }
            return null;
        };

        let match = findByName(target, preferredIndex);
        if (match) return match;

        if (fallbackNames && fallbackNames.size > 0) {
            for (const name of fallbackNames) {
                if (!name || name === target) continue;
                match = findByName(name, preferredIndex);
                if (match) return match;
            }
        }

        return null;
    };
    const [allAnswersTree, setAllAnswersTree] = useState<TreeNode[]>([]);
    const [answerIndexMap, setAnswerIndexMap] = useState<Record<string, number>>({});
    const [regeneratedAnswers, setRegeneratedAnswers] = useState<Record<string, Record<string, ChatAppResponse[]>>>({});
    const [versionIndexMap, setVersionIndexMap] = useState<Record<string, number>>({});

    const { loadThoughts } = useThoughtsLoader({
        answers,
        answerIndexMap,
        sourcePage: "feedback",
        setAnswers: setAnswers as any,
        setRegeneratedAnswers,
        setAllAnswersTree,
        setThoughtsLoadingIndex,
        setActiveAnalysisPanelTab,
        setSelectedAnswer
    });

    const [openAccessDialog, setOpenAccessDialog] = useState(false);
    const [todaySessionsRef, setTodaySessionsRef] = useState<[SessionInfo] | []>([]);
    const [last7SessionsRef, setLast7SessionsRef] = useState<[SessionInfo] | []>([]);
    const [last30SessionsRef, setLast30SessionsRef] = useState<[SessionInfo] | []>([]);
    const [monthlySessions, setMonthlySessions] = useState<YearData[]>([]);
    const [uniqueUsers, setUniqueUsers] = useState<{ key: string | number; text: string; corp_id: string }[]>([]);
    const [uniqueCorpIds, setUniqueCorpIds] = useState<{ key: string | number; text: string }[]>([]);
    const [currentIndex, setCurrentIndex] = useState<string>("");
    const [selectedCitation, setSelectedCitation] = useState(0);
    const [doctype, setDoctype] = useState<boolean>(false);
    const isTabClick = useRef<boolean>(false);
    const [histories, setHistories] = useState<Histories>({});
    const getInitialAppTypeValue = (): AppType => {
        if (appInfo.use_all_tab === true) {
            return userInfo?.apptype;
        }

        if (userInfo?.apptype !== "ALL") {
            return userInfo?.apptype;
        }

        return "FAQ";
    };

    const initialAppTypeValue = getInitialAppTypeValue();
    const [selectedAppType, setSelectedAppType] = useState<AppType>(getValidAppType(initialAppTypeValue ?? ""));
    const lastAnswerIndexRef = useRef<number>(-1);
    const clearObject = { key: "clear", text: i18next.t("filters.clear"), corp_id: "clear" };
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentSessionIdValue = useSelector(selectFeedbackCurrentSessionId);

    const Spinner = () => {
        return (
            <div className="full-page-spinner">
                <div className="spinner"></div>
            </div>
        );
    };

    function parseQueryParams(queryvalue: string) {
        const queryparameter = new URLSearchParams(queryvalue);
        const params: { [key: string]: string } = {};

        for (const [key, value] of queryparameter.entries()) {
            params[key] = value;
        }

        return params;
    }

    const updateSessionDataPoints = (answers: any) => {
        const currentSessionDataPoints: { FAQ: Array<DataPoints>; Manuals: Array<DataPoints>; ALL: Array<DataPoints> } = {
            FAQ: [],
            Manuals: [],
            ALL: []
        };
        let privateDoctype = false;
        const updatedAnswers: any = answers?.map(
            (answer: [user: string, response: { FAQ: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }]) => {
                const fAQcontext: any = answer[1]["FAQ"]?.context;
                if (fAQcontext) {
                    updateUserInfo(fAQcontext);
                    currentSessionDataPoints.FAQ.push(fAQcontext.data_points);
                }
                const manualsContext: any = answer[1]["Manuals"]?.context;
                if (manualsContext) {
                    updateUserInfo(manualsContext);
                    currentSessionDataPoints.Manuals.push(manualsContext.data_points);
                }
                const allContext: any = answer[1]["ALL"]?.context;
                if (allContext) {
                    updateUserInfo(allContext);
                    currentSessionDataPoints.ALL.push(allContext.data_points);
                }
                if (
                    fAQcontext?.data_points.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal") ||
                    manualsContext?.data_points.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal") ||
                    allContext?.data_points.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal")
                ) {
                    privateDoctype = true;
                }
                return answer;
            }
        );
        setDoctype(privateDoctype);
        return [currentSessionDataPoints, updatedAnswers];
    };

    const loadChatData = async (sessionId: string, isSideMenuItemClick: boolean) => {
        try {
            setActiveAnalysisPanelTab(undefined);
            setSessionNotFound(false);
            setIsLoading(true);
            const chatPromise = loadChatApi(sessionId, "feedback")
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    if (!result || !result.answers || result.answers.length === 0) {
                        setSessionNotFound(true);
                        setAnswers([]);
                        return;
                    }
                    if (result?.options?.showMessage === "show" && !openAccessDialog) {
                        setTimeout(() => {
                            setOpenAccessDialog(true);
                        }, 1000);
                        return;
                    }
                    setSelectedAnswer(0);
                    const [currentSessionDataPoints, updatedAnswers] = updateSessionDataPoints(result?.answers);
                    setAnswers([...updatedAnswers]);
                    setAllAnswersTree(buildTree(result.answers, result.histories));
                    setHistories(result?.histories ?? {});
                    const newMap: Record<string, number> = {};
                    Object.keys(result.histories ?? {}).forEach(id => {
                        newMap[id] = 0;
                    });
                    setVersionIndexMap(newMap);
                    setRegeneratedAnswers(result.regenerated_answers ?? {});
                    setAnswerIndexMap(
                        Object.fromEntries(
                            Object.entries(result.regenerated_answers ?? {}).flatMap(([key, appTypes]) => {
                                return Object.keys(appTypes as Record<string, ChatAppResponse[]>).map(appType => [`${key}__${appType}`, 0]);
                            })
                        )
                    );
                    dispatch(FeedBackPageComponentReducerAction.updateOverAllCurrentSessionDataPoints(currentSessionDataPoints));
                    if (sessionId && isValidUUID(sessionId)) {
                        dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(sessionId));
                        // retain apptype based on url value.
                        const queryParams = parseQueryParams(location.search);
                        const appTypeValue = isSideMenuItemClick
                            ? (getAppTypeBasedOnLastAnswerIsRelatedToAriel([...result.answers]) as AppType)
                            : getValidAppType(queryParams.AppType ?? "");
                        userInfo.apptype = appTypeValue;
                        setSelectedAppType(appTypeValue as AppType);
                        const urlParams = new URLSearchParams();
                        urlParams.append("SessionId", sessionId);
                        urlParams.append("AppType", appTypeValue);
                        navigate(`?${urlParams.toString()}`, { replace: !isSideMenuItemClick });
                    } else {
                        // if any param values are invalid fallback to default empty feedback page.
                        setCurrentIndex("");
                        const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
                        userInfo.apptype = appTypeValue;
                        setSelectedAppType(appTypeValue);
                        resetSessionData({ type: "all" });
                        dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
                        dispatch(FeedBackPageComponentReducerAction.resetOverAllCurrentSessionDataPoints());
                        navigate("/assistant/feedbacks");
                    }
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                });

            await chatPromise;
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateUserInfo = (context: any) => {
        const userEntry: any = masterList.users?.find((entry: any) => entry.key === context.userId);
        if (userEntry) {
            context.userId = userEntry.text;
        }
        const corpEntry: any = masterList.users?.find((entry: any) => entry.corp_id === context.corpId);
        if (corpEntry) {
            context.corpId = corpEntry.corp_name;
        }
        if (context?.feedback_user) {
            const feedbackUser: any = masterList.users?.find((entry: any) => entry.key === context.feedback_user);
            if (feedbackUser) {
                context.feedback_user = feedbackUser.text;
            }
            const feedbackCorpEntry: any = masterList.users?.find((entry: any) => entry.corp_id === context.feedback_corp_id);
            if (feedbackCorpEntry) {
                context.feedback_corp_id = feedbackCorpEntry.corp_name;
            }
        }
        return context;
    };

    const resetSessionData = (options: any) => {
        try {
            if (options.type === "all") {
                updateSessionStates({});
                setAnswers([]);
                setUniqueCorpIds(masterList.corps);
                setUniqueUsers([]);
            } else if (options.type === "user") {
                setUniqueUsers(filteredData.users);
                setUniqueCorpIds(filteredData.corps);
            } else {
                setUniqueCorpIds(masterList.corps);
                setUniqueUsers([]);
            }
        } catch (error) {
            console.error("Error resetting session data:", error);
        }
    };

    function applyFrontFilter(conditions: any) {
        const corpId = conditions["selectedCorpId"]?.key ?? null;
        const userId = conditions["selectedUsers"]?.key ?? null;
        setTodaySessionsRef(filterSessions(masterList.todaySessionsSource, corpId, userId));
        setLast7SessionsRef(filterSessions(masterList.last7SessionsSource, corpId, userId));
        setLast30SessionsRef(filterSessions(masterList.last30SessionsSource, corpId, userId));
        setMonthlySessions(filterYearData(masterList.monthlySessions, corpId, userId));
        return;
    }

    const handleApplyFilterClick = async (conditions: any) => {
        try {
            setIsLoading(true);
            await loadFilteredSessionsApi(conditions.filters)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    updateSessionStates(result);
                    if (result["today"]?.length > 0 || result["7days"]?.length > 0 || result["30days"]?.length > 0 || result["months"]?.length > 0) {
                        const corps: any = [];
                        const users: any = [];
                        const seenCorps = new Set();
                        masterList.users
                            .filter((item: any) => result["users_list"].includes(item.key))
                            .forEach((item: any) => {
                                if (!seenCorps.has(item.corp_id)) {
                                    corps.push({ key: item.corp_id, text: `[${item.corp_id}] ${item.corp_name}` });
                                    seenCorps.add(item.corp_id);
                                }
                                users.push({ key: item.key, text: item.text, corp_id: item.corp_id, corp_name: item.corp_name });
                            });
                        users.sort((a: any, b: any) => a.text.localeCompare(b.text));
                        filteredData.users = users;
                        filteredData.corps = corps;
                        if (conditions.isPrimaryFiltersChanged) {
                            setUniqueCorpIds(corps);
                        }
                        setUniqueUsers(users);
                    }
                    setAnswers([]);
                    setCurrentIndex("");
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                });
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const filterSessions = (sessions: any, corpId: string | null, userId: string | null) => {
        if (corpId && userId) {
            return sessions.filter((session: any) => session.corp_id === corpId && session.user_id === userId);
        } else if (corpId) {
            return sessions.filter((session: any) => session.corp_id === corpId);
        } else if (userId) {
            return sessions.filter((session: any) => session.user_id === userId);
        } else {
            return sessions;
        }
    };

    const filterYearData = (yearData: YearData[], corp_id: string, user_id: string): YearData[] => {
        return yearData
            .map(year => {
                const filteredSessions = year.sessions
                    .map(session => {
                        const filteredRecords = filterSessions(session.records, corp_id, user_id);
                        return filteredRecords.length > 0 ? { ...session, records: filteredRecords } : null;
                    })
                    .filter(session => session !== null);

                return filteredSessions.length > 0 ? { ...year, sessions: filteredSessions } : null;
            })
            .filter(year => year !== null);
    };

    const updateSessionStates = (result: any) => {
        setTodaySessionsRef(result["today"] ?? []);
        setLast7SessionsRef(result["7days"] ?? []);
        setLast30SessionsRef(result["30days"] ?? []);
        setMonthlySessions(result["months"] ?? []);
        masterList.todaySessionsSource = result["today"] ?? [];
        masterList.last7SessionsSource = result["7days"] ?? [];
        masterList.last30SessionsSource = result["30days"] ?? [];
        masterList.monthlySessions = result["months"] ?? [];
    };

    const updateUserList = (corp_id: string | null, loadFromMaster: boolean) => {
        const corpUsers: any = (loadFromMaster ? masterList.users : filteredData.users)?.filter((item: any) => item.corp_id === corp_id);
        corpUsers?.sort((a: any, b: any) => a.text.localeCompare(b.text));
        setUniqueUsers([clearObject, ...corpUsers]);
    };

    const getUniqueCorpIds = (items: any[]) => {
        return Array.from(new Set(items.map(item => item.corp_id)));
    };

    const getCorpDetails = (corp_id: any, items: any[]) => {
        const corp = items.find(item => item.corp_id === corp_id);
        return {
            key: corp_id,
            text: `[${corp_id}] ${corp.corp_name}`
        };
    };

    const loadUsers = async () => {
        try {
            await loadUsersApi()
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    let uniqueCorpIds = getUniqueCorpIds(result);
                    let filteredUsers = [];
                    if (userInfo?.role === "Consultant") {
                        uniqueCorpIds = uniqueCorpIds.filter(corp_id => userInfo?.customer_corp_ids?.some((corp: any) => corp === corp_id));
                        filteredUsers = result.filter((user: any) => uniqueCorpIds.includes(user.corp_id));
                    }
                    uniqueCorpIds = uniqueCorpIds.map(corp_id => getCorpDetails(corp_id, result));
                    masterList.users = userInfo?.role === "Consultant" ? filteredUsers : result;
                    masterList.corps = uniqueCorpIds;
                    setUniqueCorpIds(uniqueCorpIds);
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                });
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const isValidUUID = (uuid: string) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        const initializeData = async () => {
            await loadUsers();
            const params = new URLSearchParams(window.location.search);
            const sessionId = params.get("SessionId");

            if (sessionId && isValidUUID(sessionId)) {
                setCurrentIndex(sessionId);
                await loadChatData(sessionId, false);
            } else {
                setCurrentIndex("");
                dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
                dispatch(FeedBackPageComponentReducerAction.resetOverAllCurrentSessionDataPoints());
                const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
                userInfo.apptype = appTypeValue;
                setSelectedAppType(appTypeValue);
                resetSessionData({ type: "all" });
            }
        };

        initializeData();
    }, []);

    useEffect(() => {
        const queryParams = parseQueryParams(location.search);
        if (Object.keys(queryParams).length === 0) {
            setCurrentIndex("");
            const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
            userInfo.apptype = appTypeValue;
            setSelectedAppType(appTypeValue);
            resetSessionData({ type: "all" });
            dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
            dispatch(FeedBackPageComponentReducerAction.resetOverAllCurrentSessionDataPoints());
        } else if (queryParams && Object.keys(queryParams).includes("SessionId") && currentSessionIdValue !== queryParams?.SessionId) {
            const sessionId = queryParams?.SessionId;
            if (sessionId && isValidUUID(sessionId)) {
                setCurrentIndex(sessionId);
                loadChatData(sessionId, false);
            } else {
                setCurrentIndex("");
                dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
                const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
                userInfo.apptype = appTypeValue;
                setSelectedAppType(appTypeValue);
                resetSessionData({ type: "all" });
            }
        }
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("SessionId");
        const appTypeValue = params.get("AppType");
        if (sessionId && isValidUUID(sessionId) && appTypeValue) {
            const normalizedAppType = getValidAppType(appTypeValue);
            if (normalizedAppType !== String(selectedAppType) && userInfo.apptype !== normalizedAppType) {
                userInfo.apptype = normalizedAppType;
                setSelectedAppType(normalizedAppType as AppType);
            }
        }
    }, [location.search, selectedAppType]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("SessionId");
        const appTypeValue = params.get("AppType");
        if (sessionId && isValidUUID(sessionId) && appTypeValue) {
            const normalizedAppType = getValidAppType(appTypeValue);
            if (normalizedAppType !== String(selectedAppType)) {
                const urlParams = new URLSearchParams();
                urlParams.append("SessionId", sessionId);
                urlParams.append("AppType", selectedAppType);
                navigate(`?${urlParams.toString()}`);
                userInfo.apptype = selectedAppType;
            }
        }
    }, [selectedAppType]);

    const hasNonAttachmentCitations = (citations?: string[]) => (citations ?? []).some(citation => !citation.startsWith("attachment:"));

    const hasNonAttachmentCitationsForAnswer = (answer: ChatAppResponse | undefined, appType: AppType): boolean => {
        const messageContent = typeof answer?.output_text === "string" ? answer.output_text : "";
        if (!messageContent) return false;
        const context = answer?.context;
        const dataPoints = context?.data_points ?? [];
        const fileMapping = context?.file_mapping;
        const parsed = parseAnswerToHtml(messageContent, false, () => undefined, dataPoints, fileMapping, appType, "Feedback");
        return hasNonAttachmentCitations(parsed.citations);
    };

    const onShowCitation = (citation: string, citationIndex: number, index: number) => {
        setActiveCitation(citation);
        setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab);
        setSelectedAnswer(index);
        setSelectedCitation(citationIndex);
    };

    const onFeedbackSubmitClicked = async (feedback: AdminFeedback, chat_id: string, appTypeValue: AppType) => {
        try {
            const request: SaveResponse = {
                feedbacks: feedback
            };
            const response = await feedbackApi(request, chat_id, appTypeValue);
            if (!response.body) {
                throw Error("No response body");
            }
        } catch (e) {
            console.error("Feedback save failed:", e);
        }
    };

    const onToggleTab = async (tab: AnalysisPanelTabs, index: number) => {
        if (tab === AnalysisPanelTabs.SupportingContentTab) {
            const appTypeForPanel = selectedAppType;
            const answerForType = answers[index]?.[1]?.[appTypeForPanel];
            if (!hasNonAttachmentCitationsForAnswer(answerForType, appTypeForPanel)) {
                return;
            }
        }

        if (tab === AnalysisPanelTabs.ThoughtProcessTab && !(activeAnalysisPanelTab === tab && selectedAnswer === index)) {
            const appTypeForPanel = selectedAppType;
            const answerForType = answers[index]?.[1]?.[appTypeForPanel];
            if (answerForType?.context?.thoughts === undefined) {
                const fetched = await loadThoughts(index, appTypeForPanel, answerForType);
                if (fetched) return;
            }
        }

        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };
    const updateStreamedAnswer = (index: number, updatedAnswer: ChatAppResponse) => {
        const updatedStreamedAnswers = [...answers];
        if (index >= 0 && index < updatedStreamedAnswers.length) {
            updatedStreamedAnswers[index][1][selectedAppType] = updatedAnswer;
            setAnswers(updatedStreamedAnswers);
        }
    };

    const [secondDivHeight, setSecondDivHeight] = useState(0);
    const secondDivRef = useRef<HTMLDivElement>(null);
    const chatStreamRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (secondDivRef.current) {
            setSecondDivHeight(secondDivRef.current.clientHeight);
        }
        const handleSecondDivResize = () => {
            if (secondDivRef.current) {
                setSecondDivHeight(secondDivRef.current.clientHeight);
            }
        };
        window.addEventListener("resize", handleSecondDivResize);
        return () => {
            window.removeEventListener("resize", handleSecondDivResize);
        };
    }, []);

    const AccessDeniedDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{i18next.t("homepage.accessDenied")}</DialogTitle>
            <DialogContent>{i18next.t("homepage.notAuthorizedSessionMessage")}</DialogContent>
            <DialogActions>
                <Button onClick={onClose} autoFocus>
                    {i18next.t("homepage.onOK")}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const nextVersionIndexMap = (prev: Record<string, number>, parentMessageId: string, versionIndex: number, answerIndex: number): Record<string, number> => {
        if (answerIndex === 0) {
            const newMap: Record<string, number> = {};
            for (const key in prev) {
                newMap[key] = 0;
            }
            newMap[parentMessageId] = versionIndex;
            return newMap;
        }
        return { ...prev, [parentMessageId]: versionIndex };
    };

    const applyVersionChange = (versionIndex: number, parentMessageId: string, answerIndex: number) => {
        setActiveAnalysisPanelTab(undefined);
        setVersionIndexMap(prev => nextVersionIndexMap(prev, parentMessageId, versionIndex, answerIndex));
        const targetAnswers: any[] =
            versionIndex === 0 && answerIndex === 0
                ? allAnswersTree.map((root: any) => [root.record.question, root.record.answer])
                : loadAnswerRowsFromTree(answers, answerIndex, allAnswersTree, parentMessageId, versionIndex);
        updateDataPointsAndAnswers(targetAnswers);
    };

    const applyAnswerVersionChange = (currentVersion: number, question_id: string) => {
        setActiveAnalysisPanelTab(undefined);
        setAnswerIndexMap(prev => ({
            ...prev,
            [`${question_id}__${selectedAppType}`]: currentVersion
        }));
        const answerToBeAdded: ChatAppResponse | undefined = regeneratedAnswers[question_id]?.[selectedAppType]?.[currentVersion];
        const updatedAnswers: any = answers.map(ans => {
            if (ans[0].question_id === question_id) {
                return [
                    { ...ans[0] },
                    {
                        ...ans[1],
                        [selectedAppType]: answerToBeAdded
                    }
                ];
            }
            return ans;
        });
        updateDataPointsAndAnswers(updatedAnswers);
    };

    const updateDataPointsAndAnswers = (targetAnswers: any) => {
        const [currentSessionDataPoints, updatedAnswers] = updateSessionDataPoints(targetAnswers);
        dispatch(FeedBackPageComponentReducerAction.updateOverAllCurrentSessionDataPoints(currentSessionDataPoints));
        setAnswers([...updatedAnswers]);
    };

    const scrollToQuestion = (index: number) => {
        const el = document.getElementById(`feedback-qa-${index}`);
        const container = chatStreamRef.current;
        if (el && container) {
            const containerRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            container.scrollTo({ top: container.scrollTop + elRect.top - containerRect.top - 20, behavior: "smooth" });
        }
    };

    const scrollToQuestionById = (questionId: string) => {
        const index = answers.findIndex(a => a[0].question_id === questionId);
        if (index >= 0) {
            scrollToQuestion(index);
        }
    };

    // Resolve product filter keys to human-readable names using the same
    // appInfo.product_categories lookup that AnswerIcon uses.
    // Read locale at render time so it is captured as a dependency — sessionStorage
    // is not reactive, but this value is re-evaluated on every render and will be
    // fresh after a re-render triggered by a locale change.
    const selectedLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
    const productCategories = appInfo.product_categories;
    const sessionProducts = useMemo(() => {
        const productCats: any[] = productCategories?.[selectedLocale] ?? [];

        const keyToName = (key: string | null | undefined): string => {
            const k = (key || "").trim();
            if (!k) return "";
            const match = productCats.find((item: any) => (item.faq_key && item.faq_key === k) || (item.manual_key && item.manual_key === k));
            return ((match?.text || k) as string).trim().normalize("NFC");
        };

        const seen = new Set<string>();
        for (const answer of answers) {
            for (const appType of Object.keys(answer[1]) as AppType[]) {
                const pf = answer[1][appType]?.context?.product_filter;
                if (!pf) continue;
                const faqName = keyToName(pf.faq_key);
                const manualName = keyToName(pf.manual_key);
                if (faqName) seen.add(faqName);
                if (manualName) seen.add(manualName);
            }
        }
        return Array.from(seen).sort((a, b) => a.localeCompare(b));
    }, [answers, selectedLocale, productCategories]);

    // Latest conversation date: take the most recent non-empty conversationDate across all answers.
    const lastConversationDate = useMemo(() => {
        let latest = "";
        for (const answer of answers) {
            for (const appType of Object.keys(answer[1]) as AppType[]) {
                const date = answer[1][appType]?.context?.conversationDate ?? "";
                if (date && (!latest || new Date(date) > new Date(latest))) {
                    latest = date;
                }
            }
        }
        return latest || undefined;
    }, [answers]);

    // Corp ID and user name: read from the first answer that has a non-empty value,
    // checking all app types so the info is available regardless of the active tab.
    const { sessionCorpId, sessionUserName } = useMemo(() => {
        for (const answer of answers) {
            for (const appType of Object.keys(answer[1]) as AppType[]) {
                const ctx = answer[1][appType]?.context;
                const corpId = (ctx?.corpId ?? "").trim();
                const userId = (ctx?.userId ?? "").trim();
                if (corpId || userId) {
                    return { sessionCorpId: corpId, sessionUserName: userId };
                }
            }
        }
        return { sessionCorpId: "", sessionUserName: "" };
    }, [answers]);

    return (
        <div className={styles.overall_div_feedback}>
            {isLoading && <Spinner />}
            <>
                <Sidebar
                    sourcePage={"feedback"}
                    todaySessionsRef={todaySessionsRef}
                    last7SessionsRef={last7SessionsRef}
                    last30SessionsRef={last30SessionsRef}
                    monthlySessions={monthlySessions}
                    isLoading={isLoading}
                    currentIndex={currentIndex}
                    loadChatData={(session_id: string) => {
                        loadChatData(session_id, true);
                    }}
                    setCurrentIndex={(session_id: string) => {
                        setCurrentIndex(session_id);
                    }}
                    nextDate={""}
                    loadSessionsData={() => {}}
                    clearChat={() => {}}
                    isSessionLoading={false}
                    isAdmin={true}
                    clearSearchChat={() => {}}
                    setTodaySessions={() => {}}
                    setLast7DaySessions={() => {}}
                    setLast30DaySessions={() => {}}
                />

                <div className={styles.container}>
                    <div className={styles.filters_container} ref={secondDivRef}>
                        <div className={styles.feedbackHeaderLeft}>
                            <Filters
                                ref={filtersRef}
                                corpIdList={uniqueCorpIds}
                                usersList={uniqueUsers}
                                handleApplyFilterClick={filters => {
                                    handleApplyFilterClick(filters);
                                }}
                                applyFrontFilter={values => {
                                    applyFrontFilter(values);
                                }}
                                resetFilters={(type: any) => {
                                    resetSessionData(type);
                                }}
                                updateUserList={(corpId: string, isApplied: boolean) => {
                                    updateUserList(corpId, isApplied);
                                }}
                            />
                        </div>
                        <div className={styles.feedbackHeaderRight}>
                            <SessionSummaryPanel
                                isVisible={Boolean(currentIndex)}
                                sessionId={currentIndex && currentSessionIdValue === currentIndex ? currentIndex : ""}
                                conversationCount={answers.length}
                                onTopicClick={scrollToQuestionById}
                                corpId={sessionCorpId}
                                userName={sessionUserName}
                                products={sessionProducts}
                                lastConversationDate={lastConversationDate}
                            />
                        </div>
                    </div>
                    {sessionNotFound ? (
                        <SessionNotFound
                            open={sessionNotFound}
                            sourcePage="feedback"
                            onConfirm={() => {
                                resetSessionData({ type: "all" });
                                if (filtersRef.current) {
                                    filtersRef.current.resetFields();
                                }
                                setSessionNotFound(false);
                                setCurrentIndex("");
                                const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
                                userInfo.apptype = appTypeValue;
                                setSelectedAppType(appTypeValue);
                                dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
                                dispatch(FeedBackPageComponentReducerAction.resetOverAllCurrentSessionDataPoints());
                                navigate("/assistant/feedbacks");
                            }}
                        />
                    ) : (
                        <div
                            className={styles.chatRoot}
                            style={{
                                marginTop: `${secondDivHeight}px`
                            }}
                        >
                            <div className={styles.chatContainer}>
                                <div className={styles.chatMessageStream} ref={chatStreamRef}>
                                    {answers.map((answer, index) => {
                                        const currentResponseForAppType = answer[1][selectedAppType];
                                        const isFeedbackUnavailable = currentResponseForAppType?.context?.feedback?.category === 0;
                                        const computedSelectedAppType =
                                            isTabClick.current === true
                                                ? selectedAppType
                                                : answer[1] && Object.keys(answer[1]).length === 1 && answer[1]["ALL"] && appInfo?.use_all_tab === true
                                                  ? isWapUser
                                                      ? selectedAppType
                                                      : "ALL"
                                                  : selectedAppType !== "ALL"
                                                    ? selectedAppType
                                                    : appInfo.use_all_tab === true
                                                      ? "ALL"
                                                      : "FAQ";
                                        const showFollowupQuestions = useSuggestFollowupQuestions && answer.length - 1 === index;
                                        const isLastAnswer = answers?.length === 0 || (answers?.length > 0 && answers?.length - 1 === index) ? true : false;

                                        return (
                                            <div key={index} id={`feedback-qa-${index}`}>
                                                <UserChatMessage
                                                    ref={el => {
                                                        userChatMessageRefs.current[index] = el;
                                                    }}
                                                    message={answer[0].extractedQuestion}
                                                    isAdminUser={true}
                                                    selectedAppType={appInfo?.use_all_tab === true ? "ALL" : "FAQ"}
                                                    templateId={answer[0].templateId}
                                                    questionContent={answer[0].question}
                                                    sourcePage={"feedback"}
                                                    fileAttachments={answer[0].filesAttached}
                                                    searchTerm={""}
                                                    setSelectedAppType={setSelectedAppType}
                                                    currentVersionIndex={
                                                        versionIndexMap[
                                                            (answer[0]?.parentMessageId && answer[0].parentMessageId !== "0"
                                                                ? answer[0].parentMessageId
                                                                : answer[0].question_id) ?? ""
                                                        ] || 0
                                                    }
                                                    onVersionChange={(versionIndex: number, parentMessageId: string, answerIndex: number) => {
                                                        applyVersionChange(versionIndex, parentMessageId, answerIndex);
                                                    }}
                                                    index={index}
                                                    answers={answers}
                                                    histories={histories}
                                                />

                                                <div className={styles.chatMessageGpt}>
                                                    {currentResponseForAppType === undefined || currentResponseForAppType === null ? (
                                                        <div className={styles.chatMessageGptMinWidth}>
                                                            <AnswerRegenarate
                                                                selectedAppType={selectedAppType}
                                                                isLoading={false}
                                                                isStreaming={false}
                                                                answer={answer[1]}
                                                                conversationDate={answer[0]?.conversation_date}
                                                                onRetry={() => {}}
                                                                setSelectedAppType={setSelectedAppType}
                                                                onTabClicked={() => {
                                                                    setSelectedCitation(0);
                                                                    setSelectedAnswer(index);
                                                                    setActiveAnalysisPanelTab(undefined);
                                                                    isTabClick.current = true;
                                                                }}
                                                                primaryId={index.toString()}
                                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                                doctype={doctype}
                                                                isFeedbackPage={true}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <UnifiedAnswer
                                                            key={index}
                                                            response={answer[1]}
                                                            conversationDate={answer[0]?.conversation_date}
                                                            doctype={doctype}
                                                            isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                            onCitationClicked={(c: string, citationIndex: number) => onShowCitation(c, citationIndex, index)}
                                                            onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                            onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                            showFollowupQuestions={showFollowupQuestions}
                                                            isLastAnswer={isLastAnswer}
                                                            isFeedbackPage={true}
                                                            onSend={() => {}}
                                                            isLoading={false}
                                                            selectedAppType={computedSelectedAppType}
                                                            setSelectedAppType={setSelectedAppType}
                                                            lastAnswerIndexRefValue={lastAnswerIndexRef.current}
                                                            currentPageView={"history"}
                                                            onTabClicked={() => {
                                                                setSelectedCitation(0);
                                                                setSelectedAnswer(index);
                                                                isTabClick.current = true;
                                                                if (activeAnalysisPanelTab === AnalysisPanelTabs.ThoughtProcessTab) {
                                                                    setActiveAnalysisPanelTab(undefined);
                                                                }
                                                            }}
                                                            isStreaming={false}
                                                            primaryId={index.toString()}
                                                            updateStreamedAnswer={isFeedbackUnavailable ? updateStreamedAnswer : () => {}}
                                                            onFeedbackSubmitClicked={
                                                                isFeedbackUnavailable
                                                                    ? (feedback: any, conv_id: string, appTypeValue: AppType) =>
                                                                          onFeedbackSubmitClicked(feedback, conv_id, appTypeValue)
                                                                    : undefined
                                                            }
                                                            onFollowupQuestionClicked={undefined} // was explicitly undefined in streaming branch
                                                            onClickLikeDislike={
                                                                isFeedbackUnavailable
                                                                    ? () => {
                                                                          lastAnswerIndexRef.current = index;
                                                                      }
                                                                    : () => {}
                                                            }
                                                            isAdminUser={isAdmin}
                                                            isHistory={isFeedbackUnavailable ? false : true}
                                                            isRegenerateActive={false}
                                                            currentAnswerIndex={answerIndexMap?.[`${answer[0]["question_id"]}__${selectedAppType}`] ?? 0}
                                                            onAnswerVersionChange={(newVersion: number) => {
                                                                applyAnswerVersionChange(newVersion, answer[0]["question_id"] as string);
                                                            }}
                                                            answerVersions={
                                                                regeneratedAnswers[answer[0]["question_id"] as string]?.[selectedAppType]?.length ?? 0
                                                            }
                                                            onAttachmentLinkClick={(filename: string, page?: number) => {
                                                                const currentFiles = answers[index]?.[0]?.filesAttached ?? [];
                                                                const dataPoints = answers[index]?.[1]?.[selectedAppType]?.context?.data_points ?? [];
                                                                const fallbackNames =
                                                                    Array.isArray(currentFiles) && currentFiles.length === 0
                                                                        ? extractAttachmentNamesFromDataPoints(dataPoints)
                                                                        : undefined;
                                                                const match = findAttachmentOwnerIndex(filename, index, fallbackNames);
                                                                const targetIndex = match?.index ?? index;
                                                                const normalized = normalizeAttachmentName(filename);
                                                                const targetName = match?.matchedName ?? (normalized || filename);
                                                                userChatMessageRefs.current[targetIndex]?.openAttachmentPreviewByName(targetName, page);
                                                            }}
                                                            questionFilesAttached={answer[0].filesAttached}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <div style={{ position: "relative", top: "30%", fontSize: "xx-large", opacity: 0.5, fontStyle: "oblique", left: "30%" }}>
                                        {answers.length == 0 && !isLoading && <Label>{i18next.t("filters.nodata")}</Label>}
                                    </div>
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            </div>
                            {answers.length > 0 && activeAnalysisPanelTab && answers[selectedAnswer]?.[1]?.[selectedAppType] && (
                                <AnalysisPanel
                                    className={styles.chatAnalysisPanel}
                                    activeCitation={activeCitation}
                                    onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                                    citationHeight="810px"
                                    answer={answers[selectedAnswer][1][selectedAppType]}
                                    activeTab={activeAnalysisPanelTab}
                                    index={selectedCitation}
                                    isAdminUser={true}
                                    onActiveContentChanged={citationIndex => {
                                        setSelectedCitation(citationIndex);
                                    }}
                                    isStreaming={false}
                                    sourcePage={"Feedback"}
                                    isThoughtsLoading={
                                        activeAnalysisPanelTab === AnalysisPanelTabs.ThoughtProcessTab && thoughtsLoadingIndex === selectedAnswer
                                    }
                                    appType={selectedAppType}
                                />
                            )}
                        </div>
                    )}
                </div>
                <AccessDeniedDialog
                    open={openAccessDialog}
                    onClose={() => {
                        setOpenAccessDialog(false);
                        setCurrentIndex("");
                        const appTypeValue = appInfo?.use_all_tab ? "ALL" : "FAQ";
                        userInfo.apptype = appTypeValue;
                        setSelectedAppType(appTypeValue);
                        resetSessionData({ type: "all" });
                        dispatch(FeedBackPageComponentReducerAction.updateCurrentSessionId(null));
                        navigate("/assistant/feedbacks");
                    }}
                />
            </>
        </div>
    );
};

export default Feedback;
